import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  Check,
  ChevronDown,
  CircleDollarSign,
  FileSearch,
  LayoutTemplate,
  MessageCircle,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
} from 'lucide-react';

import { ContactSection } from '@/components/contact-section';
import { PublicBridgeHero } from '@/components/public-bridge-hero';
import { PublicFloatingWhatsApp } from '@/components/public-floating-whatsapp';
import { PublicSiteHeader } from '@/components/public-site-header';
import { canonicalSiteUrl } from '@/config';
import { whatsappUrl } from '@/lib/whatsapp';
const emailAddress = 'info@doqcreative.com';

const services = [
  {
    title: '網頁設計',
    body: '很多公司做完網站後，才發現內容次序混亂、重點不突出。渡橋會先整理服務和查詢路徑，再設計公司網站、服務網站或 Landing Page。',
    icon: LayoutTemplate,
    href: '/services/web-design',
  },
  {
    title: 'SEO 搜尋優化',
    body: '有些網站文章不少，但服務頁沒有對準客人真正會搜尋的問題。渡橋會把關鍵字、頁面分工、內容更新和持續優化連起來。',
    icon: Search,
    href: '/services/seo',
  },
  {
    title: 'Google Ads 搜尋廣告',
    body: '客人主動搜尋服務時，關鍵字、廣告文案和 Landing Page 要直接對應需要。渡橋會把搜尋字、否定字和查詢追蹤一起規劃。',
    icon: BarChart3,
    href: '/services/google-ads',
  },
  {
    title: 'Meta 廣告代營運',
    body: 'FB、IG 和 Threads 廣告不是只追互動；渡橋會把受眾、素材、再營銷、Landing Page 和 Meta Pixel 查詢追蹤接起來。',
    icon: CircleDollarSign,
    href: '/services/meta-ads',
  },
  {
    title: 'ERP 營運系統訂製',
    body: '客戶、銷售、訂單、庫存、項目、員工、出勤、更表和出糧資料分散？渡橋會先梳理流程，再訂製適合團隊使用的 ERP 營運系統。',
    icon: ShieldCheck,
    href: '/services/erp-system-development',
  },
  {
    title: 'WhatsApp Chat Bot 訂製',
    body: '把自動回覆、FAQ、客戶資料收集、預約和人工轉接整理成清楚的 WhatsApp 查詢流程，減少重複回覆和漏跟進。',
    icon: MessageCircle,
    href: '/services/whatsapp-chatbot-development',
  },
];

const reasons = [
  {
    title: '先度方向，再做設計',
    body: '先理解你的服務、客人和競爭對手，再決定網站應該怎樣設計，不會一開始就只談顏色和版面。',
    icon: FileSearch,
  },
  {
    title: '網站、SEO、廣告一起規劃',
    body: '網站架構、關鍵字、廣告落地頁和查詢路徑同時考慮，避免上線後才發現接不到流量。',
    icon: Sparkles,
  },
  {
    title: '適合香港本地中小企',
    body: '理解本地服務業、專業公司和新品牌常見限制，做法直接，溝通不用兜大圈。',
    icon: MessageCircle,
  },
  {
    title: '移動端優先',
    body: '很多客人第一次看你網站，都是在手機上完成搜尋、比較和查詢。版面要先照顧細屏閱讀，重點要快見到，WhatsApp 按鈕、表單入口和載入速度也要一開始處理好。',
    icon: BarChart3,
  },
];

const cases = [
  {
    title: '本地服務網站',
    body: '適合維修、工程、美容、清潔、教育等本地服務。重點是講清服務範圍、收費方向、地區覆蓋和 WhatsApp 查詢入口。',
    image: '/images/case-local-service.webp',
    srcSet: '/images/case-local-service-640.webp 640w, /images/case-local-service-800.webp 800w, /images/case-local-service.webp 1066w',
    alt: '本地服務網站示例圖，展示服務頁面、查詢按鈕和手機版畫面',
    href: '/services/local-service-website',
  },
  {
    title: '專業服務公司網站',
    body: '適合顧問、會計、法律、醫療、B2B 服務公司。用清晰架構展示專業背景、服務流程、案例和客戶信任感。',
    image: '/images/case-professional-service.webp',
    srcSet: '/images/case-professional-service-640.webp 640w, /images/case-professional-service-800.webp 800w, /images/case-professional-service.webp 1067w',
    alt: '專業服務公司網站示例圖，展示可信任的服務架構和桌面手機版畫面',
    href: '/services/professional-service-website',
  },
  {
    title: 'Google / Meta 廣告落地頁',
    body: '為 Google Ads、Meta 廣告或短期推廣而設計。頁面會集中一個服務、一個明確 CTA，並加入 Meta Pixel 和查詢追蹤，方便判斷廣告是否有效。',
    image: '/images/case-google-ads.webp',
    srcSet: '/images/case-google-ads-640.webp 640w, /images/case-google-ads-800.webp 800w, /images/case-google-ads.webp 1066w',
    alt: 'Google Ads 與 Meta 廣告落地頁示例圖，展示廣告流量、轉化表單和數據追蹤',
    href: '/services/google-ads-landing-page',
  },
  {
    title: '舊網站重整',
    body: '適合已有網站但內容太散、手機版難用或查詢少的公司。重新整理頁面次序、服務說法、SEO 基礎和查詢路徑。',
    image: '/images/case-website-refresh.webp',
    srcSet: '/images/case-website-refresh-640.webp 640w, /images/case-website-refresh.webp 761w',
    alt: '舊網站重整示例圖，展示改版前後的匿名網站結構比較',
    href: '/services/website-redesign',
  },
];

const seoItems = [
  {
    title: '客人會搜尋什麼',
    body: '先分清品牌字、服務字和問題字，不要把所有流量都推到同一頁。',
    icon: Search,
  },
  {
    title: '哪一頁負責承接',
    body: '主頁、服務頁、文章或 Landing Page 要各有任務，避免內容重複又說不清。',
    icon: LayoutTemplate,
  },
  {
    title: '看完後怎樣查詢',
    body: 'CTA、FAQ、案例和聯絡入口要放在合理位置，讓有意向的人容易行下一步。',
    icon: Target,
  },
];

const testimonials = [
  {
    quote:
      '原本只想換一個靚啲的網站，做完先發現最有用是前期幫我們整理服務次序和查詢流程，客人看得明顯清楚好多。',
    name: '本地服務公司負責人',
    detail: '網站設計',
  },
  {
    quote:
      '舊網站有很多內容，但搜尋和廣告都接不住。渡橋幫我們拆開不同服務頁，Landing Page 也更容易追蹤查詢來源。',
    name: '專業服務團隊',
    detail: '公司網站重整',
  },
  {
    quote:
      '溝通很直接，會指出哪些內容其實不需要放，哪些位置要講清楚。手機版查詢路徑做得比以前順手。',
    name: '新品牌創辦人',
    detail: '品牌網站與內容規劃',
  },
];

const pricingPlans = [
  {
    name: '單頁網站',
    price: '$999',
    unit: '起',
    eyebrow: 'Landing Page / One-page site',
    badge: '嘗鮮價',
    description:
      '適合剛開始推一項服務、活動頁、廣告落地頁，或者想先用一頁清楚介紹生意。',
    features: [
      '單頁版面設計與製作',
      '手機版與桌面版製作',
      '基本服務文案整理',
      'WhatsApp 查詢按鈕',
      '基本 SEO 標題與描述',
      '上線前速度與顯示檢查',
      '一年網站寄存',
    ],
    addons: ['增加一個語言版本 +$500'],
    note: '適合內容不多、想快啲有一個可用頁面的公司。',
  },
  {
    name: '公司網站 + CMS',
    price: '$3,999',
    unit: '起',
    eyebrow: 'Company website / CMS',
    description:
      '適合需要多個頁面、日後自己更新內容，或者想正式整理公司形象和服務架構。',
    features: [
      '首頁、關於、服務、聯絡等基本頁面',
      '後臺 CMS 內容管理',
      '服務頁架構與內容整理',
      '手機版與桌面版製作',
      '基本 SEO 架構設定',
      '表單或 WhatsApp 查詢入口',
      '圖片壓縮與基本速度優化',
      'Google Analytics / Search Console 基本設定',
      '網站上線與基本教學',
      '一年網站寄存',
    ],
    note: '適合公司網站重整、專業服務公司、本地服務品牌。',
    featured: true,
  },
  {
    name: 'Google Ads / Meta 廣告代營運',
    price: '按月報價',
    unit: '月',
    eyebrow: 'Google Ads / Meta Ads management',
    description:
      '適合需要持續投放 Google 搜尋廣告、FB／IG／Threads Meta 廣告，並希望把 Landing Page、查詢追蹤和預算優化放在同一個工作範圍的公司。',
    features: [
      'Google Ads 關鍵字、Campaign 與廣告文案管理',
      'Meta 廣告受眾、素材方向與再營銷規劃',
      'Landing Page、Meta Pixel 和查詢追蹤檢查',
      '按搜尋字、受眾、素材和查詢質素持續優化',
      '每月成效整理與下一步建議',
    ],
    note: '廣告媒體費另計；月費按廣告渠道、Campaign 數量、投放範圍和優化節奏確認。',
  },
];

const faqs = [
  {
    q: '「渡橋」是不是等於網站顧問？',
    a: '可以這樣理解一部分，但我們不只是給意見。渡橋會先幫你整理方向，再落到網站架構、文案、設計、SEO 和查詢流程，最後做成可以上線使用的頁面。',
  },
  {
    q: '做一個網站需要幾耐？',
    a: '如果方向清楚、資料齊全，單頁網站或簡單公司網站最快可以一星期內完成。開始前我們會先列清楚頁面、內容和交付節點，避免做到一半才發現缺資料。',
  },
  {
    q: '如果我已有舊網站，可以只重做部分頁面嗎？',
    a: '可以。很多時候未必需要全站推倒重來，可以先重整主頁、服務頁或廣告落地頁，把最影響查詢的部分先做好。',
  },
  {
    q: 'SEO 是不是網站做好後才開始？',
    a: '最好不是。SEO 應該在網站規劃時已經一起想，包括關鍵字、頁面分工、標題、內容和內部連結。等網站做好才補，通常會改得更多。',
  },
  {
    q: 'Google Ads 或 Meta 廣告需要每月很多預算嗎？',
    a: '不一定。預算要看行業、服務價值和推廣目標。比起一開始追大預算，更重要是先做好 Landing Page、Meta Pixel 和查詢追蹤，知道錢花到哪裡。',
  },
  {
    q: '可以先做一頁 Landing Page 嗎？',
    a: '可以，特別適合新服務、短期推廣或想先測試市場反應的情況。一頁也要有清楚結構：客人問題、你的服務、信任資料、FAQ 和 WhatsApp 查詢。',
  },
  {
    q: '網站做好後要不要做 SEO？',
    a: '如果希望客人日後透過 Google 找到你，就應持續檢查搜尋字、服務頁內容、內部連結和技術狀況。基本 SEO 是起點，不等於完成後便會自動有排名。',
  },
  {
    q: '單頁網站和公司網站哪個更適合我？',
    a: '只推一項服務、活動或想先測試市場，可以由單頁網站開始；有多項服務、需要案例、文章或日後自行更新內容，較適合公司網站連 CMS。',
  },
  {
    q: '改版網站和重新做網站差多少？',
    a: '如果舊網站技術仍可用，可以先重整首頁、服務頁和手機版；如果架構、後臺、速度和內容都已過時，整站重做通常較有效率。評估後才會決定範圍。',
  },
  {
    q: '廣告和 SEO 可以同時做嗎？',
    a: '可以，而且兩者可以共用清楚的服務定位和 Landing Page。廣告較快取得流量，SEO 用較長時間累積搜尋能見度；重點是分清預算、頁面和量度方式。',
  },
  {
    q: 'ERP 系統訂製包含甚麼內容？',
    a: '可包括流程分析、原型、核心模組開發、測試、上線、資料遷移規劃和持續功能迭代。實際範圍會按功能、角色、整合和資料情況列明。',
  },
  {
    q: 'WhatsApp Chat Bot 訂製可以處理預約嗎？',
    a: '可以先處理常見問題、資料收集、可約時段、確認和人工轉接。Google Calendar、CRM 或其他平台整合會按 API、帳戶權限和實際流程另行確認。',
  },
  {
    q: '開始前需要準備好全部文案和圖片嗎？',
    a: '不用。先準備公司資料、主要服務、目標客人、現有網站和參考方向便可以開始。渡橋會列出仍欠的內容，並協助整理成頁面可用的次序。',
  },
];

function HomePage() {
  return (
    <div className="min-h-screen bg-doq-canvas text-doq-ink">
      <div className="mx-auto max-w-[1540px] border-x border-[#143d35]/16 bg-doq-surface shadow-[0_0_0_1px_rgba(20,61,53,0.04)]">
        <PublicSiteHeader locale="zh" />
        <main id="main-content">
          <HeroSection />
          <ServicesSection />
          <ReasonsSection />
          <PricingSection />
          <SeoSection />
          <CasesSection />
          <TestimonialsSection />
          <FaqSection />
          <ContactSection
            locale="zh"
            title="聯絡我們"
            description="想重做網站、開一頁 Landing Page，或者未肯定應該先做 SEO 還是廣告，可以先傳現有網站或參考網站俾我哋。渡橋會先幫你睇清方向，再建議下一步點做。"
          />
        </main>
        <Footer />
      </div>
      <PublicFloatingWhatsApp locale="zh" />
    </div>
  );
}

function WhatsAppButton({ className = '' }: { className?: string }) {
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2 bg-[#815321] font-semibold text-white transition hover:bg-[#6f451c] active:translate-y-px ${className}`}
    >
      WhatsApp 查詢
      <ArrowRight className="size-4" strokeWidth={1.8} />
    </a>
  );
}

function HeroSection() {
  return (
    <PublicBridgeHero
      title={'未諗到網站點做？\n我哋同你一齊度橋'}
      description="網站唔只係靚唔靚，而係客人搵唔搵到、睇唔睇得明，同埋願唔願意聯絡你。我哋由網站設計、SEO 到 Google / Meta Ads，一齊整理方向，做好每一步。"
      primaryLabel="WhatsApp 查詢"
      primaryHref={whatsappUrl}
    />
  );
}

function LegacyServicesSection() {
  return (
    <section id="services" className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-12 lg:px-24">
      <div className="mb-6 flex flex-col justify-between gap-4 md:mb-8 md:flex-row md:items-end">
        <h2 className="text-[32px] font-semibold tracking-[-0.05em] sm:text-4xl">服務</h2>
        <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-[#173d35] hover:text-[#815321]">
          查看全部服務
          <ArrowRight className="size-4" />
        </Link>
      </div>
      <p className="mb-3 flex items-center justify-end gap-2 text-xs font-medium text-[#435049] sm:hidden">
        右滑可查看更多
        <ArrowRight className="size-4 text-[#815321]" aria-hidden="true" />
      </p>
      <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-4">
        {services.map((service) => (
          <Link
            key={service.title}
            to={service.href}
            className="group min-w-[78vw] snap-start border border-[#143d35]/18 bg-doq-card p-5 transition hover:-translate-y-1 hover:bg-white sm:min-w-0 sm:p-6 lg:min-h-[250px]"
          >
            <service.icon className="size-7 text-[#143d35] sm:size-8" strokeWidth={1.45} />
            <h3 className="mt-6 text-lg font-semibold tracking-[-0.04em] sm:mt-8 sm:text-xl">{service.title}</h3>
            <p className="mt-3 line-clamp-4 text-sm leading-6 text-[#344840] sm:mt-4 sm:line-clamp-none sm:leading-7">{service.body}</p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#173d35] transition group-hover:text-[#815321] sm:mt-6">
              瞭解服務
              <ArrowRight className="size-4 transition group-hover:translate-x-1" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setIsVisible(true);
        observer.disconnect();
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="services"
      data-visible={isVisible}
      aria-labelledby="services-title"
      className="services-section bg-doq-surface px-4 py-10 text-doq-ink sm:px-8 sm:py-12 lg:px-24"
    >
      <div className="mb-6 flex flex-col justify-between gap-4 md:mb-8 md:flex-row md:items-end">
        <h2 id="services-title" className="text-[32px] font-semibold tracking-[-0.05em] sm:text-4xl">服務</h2>
        <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold !text-[#173d35] transition-colors hover:!text-[#815321]">
          查看全部服務
          <ArrowRight className="size-4" />
        </Link>
      </div>
      <p className="hidden">
        右滑可查看更多
        <ArrowRight className="size-4 text-[#815321]" aria-hidden="true" />
      </p>
      <div className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-8 hidden h-px bg-[#815321]/20 lg:block" aria-hidden="true">
          <span className="service-route-line block h-full origin-left bg-[#815321]" />
        </div>
        <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-3 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-x-5 sm:gap-y-8 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-[1.35fr_1fr_1fr] lg:gap-x-8 lg:gap-y-12">
          {services.map((service, index) => {
            const isRowEnd = index % 3 === 2;
            const isLast = index === services.length - 1;
            const isFeatured = index === 0;

            return (
              <Link
                key={service.title}
                to={service.href}
                style={{ animationDelay: `${index * 90}ms` }}
                className={`service-route-card group relative min-w-[78vw] snap-start border-t border-[#143d35]/18 bg-doq-card px-1 pb-6 pt-10 text-[#102d28] transition-[background-color,border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#815321]/55 sm:min-w-0 sm:px-2 sm:pb-7 lg:min-h-[230px] lg:pt-12 ${isFeatured ? 'service-route-card--light row-span-2 border border-[#143d35]/18 p-6 sm:p-8 lg:min-h-[520px]' : 'service-route-card--dark'} ${isFeatured ? 'lg:row-span-2' : ''}`}
              >
                <span className="absolute left-5 top-[-5px] size-2.5 rounded-full border-2 border-doq-surface bg-doq-copper transition-transform duration-300 group-hover:scale-125 lg:left-6" aria-hidden="true" />
                <span className={`pointer-events-none absolute left-7 right-[-13px] top-[-1px] h-px bg-[#815321]/35 transition-colors duration-300 group-hover:bg-[#815321] lg:left-8 lg:right-[-20px] ${isRowEnd || isLast ? 'lg:hidden' : ''}`} aria-hidden="true" />
                <service.icon className={`text-[#143d35] transition-colors duration-300 group-hover:text-[#815321] ${isFeatured ? 'size-10 sm:size-12' : 'size-8 sm:size-9'}`} strokeWidth={1.45} />
                <h3 className={`mt-6 font-semibold tracking-[-0.04em] text-[#102d28] sm:mt-8 ${isFeatured ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-xl'}`}>{service.title}</h3>
                <p className="mt-3 line-clamp-4 text-sm leading-6 text-[#344840] sm:mt-4 sm:line-clamp-none sm:leading-7">{service.body}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold !text-[#173d35] transition-colors duration-300 group-hover:!text-[#815321] sm:mt-6">
                  查看服務
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
                <span className="mt-6 flex items-center gap-1 border-t border-[#143d35]/12 pt-3 text-[11px] font-medium !text-[#815321] sm:hidden">
                  右滑查看更多
                  <ArrowRight className="size-3" aria-hidden="true" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ReasonsSection() {
  return (
    <section className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-12 lg:px-24">
      <div className="grid gap-7 lg:grid-cols-[0.88fr_1.12fr] lg:gap-8">
        <div>
          <h2 className="text-balance text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:text-4xl">識設計，也要識幫你度橋</h2>
          <p className="mt-4 max-w-xl text-[15px] leading-7 text-[#344840] sm:mt-5 sm:text-base sm:leading-8">
            網站問題很多時不是顏色不夠靚，而是內容次序不清楚、重點太散、客人看不到信任感，或者 SEO 和廣告一開始沒有一起想。渡橋會先理解你的服務和客人，再決定網站應該怎樣設計。
          </p>
          <div className="relative mx-auto mt-7 max-w-[240px] sm:mt-12 sm:max-w-[390px] lg:ml-4 lg:mt-16">
            <div className="absolute inset-x-10 bottom-8 h-20 rounded-full bg-[#143d35]/8 blur-2xl" aria-hidden="true" />
            <img
              src="/images/doq-thinking-consultant-420.webp"
              srcSet="/images/doq-thinking-consultant-420.webp 420w, /images/doq-thinking-consultant-640.webp 640w, /images/doq-thinking-consultant.webp 720w"
              sizes="(min-width: 640px) 380px, 240px"
              alt="正在思考網站方向的渡橋顧問插畫"
              width="420"
              height="492"
              className="relative mx-auto h-auto w-full max-w-[240px] object-contain sm:max-w-[380px]"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
        <div className="grid gap-px bg-[#143d35]/14 sm:grid-cols-2">
          {reasons.map((reason) => (
            <article key={reason.title} className="bg-doq-card p-5 sm:p-7">
              <reason.icon className="size-7 text-[#143d35] sm:size-8" strokeWidth={1.45} />
              <h3 className="mt-4 text-lg font-semibold tracking-[-0.04em] sm:mt-6 sm:text-xl">{reason.title}</h3>
              <p className="mt-2 text-sm leading-6 text-[#344840] sm:mt-3 sm:leading-7">{reason.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CasesSection() {
  return (
    <section id="cases" className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-12 lg:px-24">
      <div className="mb-6 flex flex-col justify-between gap-4 md:mb-8 md:flex-row md:items-end">
        <h2 className="text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:text-4xl">不同生意，有不同橋要度</h2>
        <Link
          to="/cases"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#173d35] hover:text-[#815321]"
        >
          查看更多真實案例
          <ArrowRight className="size-4" />
        </Link>
      </div>
      <div className="-mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-4">
        {cases.map((item) => (
          <article key={item.title} className="group min-w-[78vw] snap-start sm:min-w-0">
            <div className="overflow-hidden border border-[#143d35]/18 bg-[#efe8dc]">
              {item.href ? (
                <Link to={item.href} aria-label={`瞭解${item.title}服務`}>
                  <CaseImage item={item} />
                </Link>
              ) : item.url ? (
                <a href={item.url} target="_blank" rel="noopener noreferrer" aria-label={`查看${item.title}案例`}>
                  <CaseImage item={item} />
                </a>
              ) : (
                <CaseImage item={item} />
              )}
            </div>
            <h3 className="mt-3 text-base font-semibold tracking-[-0.04em] sm:mt-4 sm:text-lg">
              {item.href ? (
                <Link to={item.href} className="transition hover:text-[#815321]">
                  {item.title}
                </Link>
              ) : item.url ? (
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="transition hover:text-[#815321]">
                  {item.title}
                </a>
              ) : (
                item.title
              )}
            </h3>
            <p className="mt-1 line-clamp-2 text-sm leading-6 text-[#344840] sm:line-clamp-none">{item.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-12 lg:px-24">
      <div className="grid gap-7 lg:grid-cols-[0.78fr_1.22fr] lg:gap-9">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Client feedback</p>
          <h2 className="mt-3 text-balance text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:mt-4 sm:text-4xl">
            客戶點樣講
          </h2>
          <p className="mt-4 max-w-md text-[15px] leading-7 text-[#344840] sm:mt-5 sm:text-base sm:leading-8">
            網站做得好不好，最後要看客人是否看得明、團隊是否用得順、查詢是否更容易跟進。
          </p>
        </div>
        <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-3 sm:gap-px sm:bg-[#143d35]/14 sm:overflow-visible sm:px-0 sm:pb-0">
          {testimonials.map((item) => (
            <article key={item.name} className="min-w-[82vw] snap-start border border-[#143d35]/14 bg-doq-card p-5 sm:min-w-0 sm:border-0 sm:p-7">
              <div className="text-3xl font-semibold leading-none text-[#815321] sm:text-4xl">“</div>
              <p className="mt-4 text-sm leading-6 text-[#354a43] sm:mt-5 sm:leading-7">{item.quote}</p>
              <div className="mt-6 border-t border-[#143d35]/14 pt-4 sm:mt-8 sm:pt-5">
                <p className="font-semibold tracking-[-0.03em] text-[#102d28]">{item.name}</p>
                <p className="mt-1 text-sm text-[#3f514a]">{item.detail}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function SeoSection() {
  const routeSteps = [
    ['搜尋', '香港網頁設計、服務關鍵字、地區字'],
    ['理解', '清楚看到服務範圍、做法和適合對象'],
    ['信任', '案例、FAQ、流程和真實聯絡方式'],
    ['查詢', '用 WhatsApp 直接問價、約見或瞭解下一步做法'],
  ];

  return (
    <section id="seo" className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-14 lg:px-24">
      <div className="grid gap-7 lg:grid-cols-[0.76fr_1.24fr] lg:gap-10">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Search before layout</p>
          <h2 className="mt-3 text-balance text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:mt-4 sm:text-4xl md:text-5xl">
            做網站之前，先想清楚客人會點樣搵你
          </h2>
          <p className="mt-4 max-w-xl text-[15px] leading-7 text-[#344840] sm:mt-6 sm:text-base sm:leading-8">
            香港網頁設計不只是排版和視覺。對很多中小企來說，網站要同時處理三件事：講清楚服務、讓 Google 明白頁面內容、讓有需要的客人方便查詢。
          </p>
          <p className="mt-3 max-w-xl text-[15px] leading-7 text-[#344840] sm:mt-4 sm:text-base sm:leading-8">
            渡橋會把網站設計、SEO 搜尋優化、Google Ads 和 Meta 廣告一起考慮，讓搜尋流量與社交媒體廣告都有合適頁面承接，避免網站做好後才發現內容不夠、關鍵字不對或查詢路徑不清楚。
          </p>
          <WhatsAppButton className="mt-6 h-12 w-full px-5 text-sm sm:mt-8 sm:w-auto" />
        </div>
        <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr] lg:gap-5">
          <div className="grid border border-[#143d35]/18">
            {seoItems.map((item) => (
              <article key={item.title} className="flex gap-4 border-b border-[#143d35]/14 bg-doq-card p-5 last:border-b-0 sm:gap-5 sm:p-6">
                <item.icon className="mt-1 size-5 shrink-0 text-[#815321] sm:size-6" strokeWidth={1.55} />
                <div>
                  <h3 className="font-semibold tracking-[-0.03em]">{item.title}</h3>
                  <p className="mt-1 text-sm leading-6 text-[#344840]">{item.body}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="relative overflow-hidden border border-[#143d35]/18 bg-[#123f37] p-5 text-[#fbf5ea] sm:p-6">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px]" />
            <div className="relative">
              <div className="flex items-center justify-between border-b border-white/16 pb-4">
                <span className="text-sm font-semibold">搜尋到查詢路徑</span>
                <span className="text-xs uppercase tracking-[0.18em] text-[#d9a267]">SEO / Ads / CTA</span>
              </div>
              <div className="mt-6 space-y-4 sm:mt-7 sm:space-y-5">
                {routeSteps.map(([title, body], index) => (
                  <div key={title} className="grid grid-cols-[38px_1fr] gap-3 sm:grid-cols-[44px_1fr] sm:gap-4">
                    <span className="grid size-8 place-items-center rounded-full bg-[#d9a267] text-xs font-semibold text-[#123f37] sm:size-9">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="border-b border-white/14 pb-3 sm:pb-4">
                      <p className="font-semibold tracking-[-0.02em]">{title}</p>
                      <p className="mt-1 text-sm leading-6 text-[#eef5ea]">{body}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 grid grid-cols-3 gap-2 text-center text-[11px] text-[#eef5ea] sm:mt-7 sm:text-xs">
                <span className="border border-white/16 px-2 py-3">關鍵字</span>
                <span className="border border-white/16 px-2 py-3">內容架構</span>
                <span className="border border-white/16 px-2 py-3">查詢追蹤</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section id="pricing" className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-14 lg:px-24">
      <div className="mb-7 grid gap-4 sm:mb-9 lg:grid-cols-[0.78fr_1.22fr] lg:items-end lg:gap-6">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Pricing</p>
          <h2 className="mt-3 text-balance text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:mt-4 sm:text-4xl md:text-5xl">
            先有預算方向，再決定點樣做
          </h2>
        </div>
        <p className="max-w-2xl text-[15px] leading-7 text-[#344840] sm:text-base sm:leading-8">
          每間公司需要的頁面、內容和功能都不同。以下是常見起步方案，方便你先判斷大概預算；實際報價會按頁面數量、內容準備程度和功能要求再確認。
        </p>
      </div>
      <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-px sm:bg-[#143d35]/14 sm:px-0 sm:pb-0 lg:grid-cols-3">
        {pricingPlans.map((plan) => (
          <article
            key={plan.name}
            className={`relative flex min-w-[84vw] snap-start flex-col border border-[#143d35]/14 p-5 sm:min-w-0 sm:border-0 sm:p-8 ${
              plan.featured ? 'bg-[#063d36] text-[#fbf5ea]' : 'bg-doq-card'
            }`}
          >
            <div className="sm:min-h-[238px]">
              <div className="flex min-h-7 items-start justify-between gap-5">
                <p className={`pr-2 text-xs font-semibold uppercase tracking-[0.18em] ${plan.featured ? 'text-[#d9a267]' : 'text-[#815321]'}`}>
                  {plan.eyebrow}
                </p>
                {plan.badge ? (
                  <span className="shrink-0 bg-[#815321] px-3 py-1 text-xs font-semibold tracking-[0.12em] text-white">
                    {plan.badge}
                  </span>
                ) : plan.featured ? (
                  <span className="shrink-0 border border-[#d9a267]/60 px-3 py-1 text-xs font-semibold tracking-[0.12em] text-[#f0c58b]">
                    常見選擇
                  </span>
                ) : null}
              </div>
              <h3 className="mt-5 text-xl font-semibold tracking-[-0.05em] sm:mt-6 sm:text-2xl">{plan.name}</h3>
              <div className="mt-5 flex items-end gap-2 sm:mt-7">
                <span className="text-4xl font-semibold tracking-[-0.07em] sm:text-5xl">{plan.price}</span>
                <span className={`pb-2 text-sm ${plan.featured ? 'text-[#eef5ea]' : 'text-[#344840]'}`}>{plan.unit}</span>
              </div>
              <p className={`mt-5 text-sm leading-7 ${plan.featured ? 'text-[#eef5ea]' : 'text-[#344840]'}`}>
                {plan.description}
              </p>
            </div>
            <ul className="mt-6 space-y-3 sm:mt-7 sm:min-h-[288px]">
              {plan.features.map((feature) => (
                <li key={feature} className="flex gap-3 text-sm leading-6">
                  <Check className={`mt-1 size-4 shrink-0 ${plan.featured ? 'text-[#d9a267]' : 'text-[#815321]'}`} strokeWidth={1.8} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            {plan.addons ? (
              <div className={`mt-6 border px-4 py-4 text-sm sm:mt-7 ${plan.featured ? 'border-white/22 text-[#eef5ea]' : 'border-[#143d35]/14 text-[#344840]'}`}>
                <p className="font-semibold tracking-[-0.02em] text-inherit">附加項目</p>
                <div className="mt-3 grid gap-2">
                  {plan.addons.map((addon) => (
                    <p key={addon}>{addon}</p>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="mt-auto pt-6 sm:pt-8">
              <p className={`border-t pt-5 text-sm leading-7 ${plan.featured ? 'border-white/22 text-[#eef5ea]' : 'border-[#143d35]/14 text-[#344840]'}`}>
                {plan.note}
              </p>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-6 inline-flex h-12 w-full items-center justify-center gap-2 px-5 text-sm font-semibold transition sm:mt-7 ${
                  plan.featured
                    ? 'bg-[#d9a267] text-[#063d36] hover:bg-[#efc98f]'
                    : 'bg-[#063d36] text-[#fbf5ea] hover:bg-[#0b4a42]'
                }`}
              >
                WhatsApp 查詢
                <ArrowRight className="size-4" strokeWidth={1.8} />
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function CaseImage({ item }: { item: (typeof cases)[number] }) {
  return (
    <img
      src={item.image}
      srcSet={item.srcSet}
      sizes="(min-width: 1280px) 25vw, (min-width: 640px) 50vw, 78vw"
      alt={item.alt}
      width="800"
      height="533"
      className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-[1.035]"
      loading="lazy"
      decoding="async"
    />
  );
}

function FaqSection() {
  return (
    <section id="faq" className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-12 lg:px-24">
      <h2 className="text-[32px] font-semibold tracking-[-0.05em] sm:text-4xl">常見問題</h2>
      <div className="mt-6 grid items-start gap-3 sm:mt-8 sm:gap-6 md:grid-cols-2">
        {faqs.map((faq, index) => (
          <FaqItem
            key={faq.q}
            question={faq.q}
            answer={faq.a}
            defaultOpen={false}
          />
        ))}
      </div>
    </section>
  );
}

function FaqItem({
  question,
  answer,
  defaultOpen = false,
}: {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}) {
  return (
    <details className="group self-start border border-[#143d35]/18 bg-doq-card" open={defaultOpen}>
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-4 text-left text-sm font-semibold tracking-[-0.02em] text-[#143d35] outline-none transition hover:bg-[#f3eadc] focus-visible:ring-2 focus-visible:ring-[#d9a267] focus-visible:ring-offset-2 focus-visible:ring-offset-[#fbf5ea] sm:gap-5 sm:px-6 sm:text-base [&::-webkit-details-marker]:hidden">
        <span>{question}</span>
        <ChevronDown
          className="size-5 shrink-0 text-[#815321] transition group-open:rotate-180"
          strokeWidth={1.6}
          aria-hidden="true"
        />
      </summary>
      <div className="px-4 pb-5 text-sm leading-6 text-[#344840] sm:px-6 sm:leading-7">
        {answer}
      </div>
    </details>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#143d35]/14 px-5 py-6 sm:px-8 lg:px-24">
      <div className="flex flex-col gap-3 text-center text-sm text-[#344840] md:flex-row md:items-center md:justify-between md:text-left">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Link to="/" className="font-semibold text-[#102d28]">渡橋創意有限公司</Link>
          <Link to="/terms" className="hover:text-[#815321]">服務條款</Link>
          <Link to="/privacy" className="hover:text-[#815321]">隱私政策</Link>
        </div>
        <p>© 2026 渡橋創意有限公司。保留所有權利。</p>
      </div>
    </footer>
  );
}

export const Route = createFileRoute('/')({
  head: () => {
    const title = '香港網頁設計與 SEO｜同你度橋做網站｜渡橋 doqcreative';
    const description =
      '渡橋 doqcreative 為香港中小企提供網頁設計、網站開發、SEO 搜尋優化、Google Ads 及 Meta 廣告服務，包括 FB 廣告、IG 廣告及 Threads 廣告。先同你度清楚方向，再建立清晰、可信、容易帶來查詢的網站。';
    const appUrl = canonicalSiteUrl;
    const canonicalUrl = `${appUrl}/`;
    const organizationId = `${canonicalUrl}#organization`;
    const ogImageUrl = `${appUrl}/og-image.jpg`;

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: canonicalUrl },
        { property: 'og:image', content: ogImageUrl },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:alt', content: '渡橋 Doq Creative 香港網頁設計與 SEO 預覽圖' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImageUrl },
        { name: 'twitter:image:alt', content: '渡橋 Doq Creative 香港網頁設計與 SEO 預覽圖' },
        {
          'script:ld+json': {
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': ['Organization', 'LocalBusiness', 'ProfessionalService'],
                '@id': organizationId,
                name: '渡橋 Doq Creative',
                alternateName: 'Doq Creative',
                url: canonicalUrl,
                logo: `${appUrl}/images/doq-hero-bridge.webp`,
                image: `${appUrl}/og-image.jpg`,
                email: emailAddress,
                telephone: '+85295946956',
                areaServed: { '@type': 'AdministrativeArea', name: 'Hong Kong' },
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: '英皇道367-373號上潤中心19A室',
                  addressLocality: '北角',
                  addressRegion: 'Hong Kong',
                  addressCountry: 'HK',
                },
                description: '香港網頁設計、SEO 搜尋優化、Google Ads 及 Meta 廣告服務。',
                contactPoint: {
                  '@type': 'ContactPoint',
                  contactType: 'customer service',
                  email: emailAddress,
                  telephone: '+85295946956',
                  availableLanguage: ['zh-HK', 'en'],
                },
                sameAs: [],
              },
              {
                '@type': 'WebSite',
                '@id': `${canonicalUrl}#website`,
                name: '渡橋 Doq Creative',
                url: canonicalUrl,
                inLanguage: 'zh-HK',
                publisher: { '@id': organizationId },
              },
              {
                '@type': 'FAQPage',
                '@id': `${canonicalUrl}#faq`,
                inLanguage: 'zh-HK',
                mainEntity: faqs.map((faq) => ({
                  '@type': 'Question',
                  name: faq.q,
                  acceptedAnswer: { '@type': 'Answer', text: faq.a },
                })),
              },
            ],
          },
        },
      ],
      links: [
        { rel: 'canonical', href: canonicalUrl },
        { rel: 'alternate', hrefLang: 'zh-HK', href: canonicalUrl },
        { rel: 'alternate', hrefLang: 'en', href: `${appUrl}/en` },
        { rel: 'alternate', hrefLang: 'x-default', href: canonicalUrl },
        {
          rel: 'preload',
          as: 'image',
          href: '/images/doq-hero-arch-mobile.webp',
          media: '(max-width: 639px)',
          fetchPriority: 'high',
        },
        {
          rel: 'preload',
          as: 'image',
          href: '/images/doq-hero-arch-desktop.webp',
          media: '(min-width: 640px)',
          fetchPriority: 'high',
        },
      ],
    };
  },
  component: HomePage,
});
